/**
 * Native Date utility functions to replace moment.js usage.
 * Replaces all moment.js patterns used across the project.
 */

/**
 * Format a Date object using a format string.
 * Supports: DD, MM, YYYY, YY, HH, mm
 */
export function formatDate(date, formatStr) {
  if (date == null) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const map = {
    DD: pad(d.getDate()),
    MM: pad(d.getMonth() + 1),
    YYYY: String(d.getFullYear()),
    YY: String(d.getFullYear()).slice(-2),
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
  };
  return formatStr.replace(/DD|MM|YYYY|YY|HH|mm/g, (match) => map[match] || match);
}

/**
 * Format a unix timestamp (seconds) using a format string.
 */
export function formatUnixDate(ts, formatStr = 'DD/MM/YYYY') {
  return formatDate(new Date(ts * 1000), formatStr);
}

/**
 * Format a unix timestamp (seconds) as "DD/MM/YYYY HH:mm".
 */
export function formatUnixDateTime(ts) {
  return formatDate(new Date(ts * 1000), 'DD/MM/YYYY HH:mm');
}

/**
 * Parse a string/number into a Date. Returns null for invalid dates.
 */
export function parseDate(value) {
  if (!value && value !== 0) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Parse an ISO datetime string from the API as UTC.
 * Backend guarantees UTC; if the string lacks a timezone indicator,
 * append 'Z' to force UTC interpretation (prevents DST-related shifts).
 *
 * @param {string|number} value - ISO datetime string or timestamp
 * @returns {Date|null}
 */
export function parseUTC(value) {
  if (!value && value !== 0) return null;
  // If it's a string that looks like an ISO datetime without timezone info, append Z
  if (typeof value === 'string') {
    // Matches patterns like: 2024-03-15T14:30:00 or 2024-03-15T14:30:00.123456
    // But NOT ones that already have Z, +00:00, -05:00, etc.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) && !/[+-]\d{2}:\d{2}$/.test(value) && !value.endsWith('Z')) {
      value = value + 'Z';
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date value (string or number) as "DD/MM/YYYY HH:mm".
 * Falls back to the raw value if the date is invalid.
 */
export function formatStringToDateTime(value) {
  const d = parseDate(value);
  return d ? formatDate(d, 'DD/MM/YYYY HH:mm') : value;
}

/**
 * Adjust a Date for timezone offset (preserves the "local time" display).
 * Equivalent to moment(date).format() after timezone compensation.
 */
export function adjustTimezone(date) {
  const d = date instanceof Date ? date : new Date(date);
  return new Date(d.valueOf() + d.getTimezoneOffset() * 60000);
}

/**
 * Get the number of days between two dates.
 * Positive result means date1 is before date2.
 */
export function daysDiff(date1, date2) {
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);
  return Math.floor((d2 - d1) / (24 * 60 * 60 * 1000));
}

/**
 * Get the number of years between two dates (date1 to date2).
 */
export function yearsDiff(date1, date2) {
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return NaN;
  let years = d2.getFullYear() - d1.getFullYear();
  const monthDiff = d2.getMonth() - d1.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && d2.getDate() < d1.getDate())) {
    years -= 1;
  }
  return years;
}

/**
 * Format a date as "YYYY-MM-DD".
 */
export function formatDateISO(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Format a date as "YYYY-MM-DD HH:mm".
 */
export function formatDateTimeISO(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${formatDateISO(d)} ${formatDate(d, 'HH:mm')}`;
}

/**
 * Get start of day (00:00:00.000).
 */
export function startOfDay(date) {
  const d = date instanceof Date ? date : new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get end of day (23:59:59.999).
 */
export function endOfDay(date) {
  const d = date instanceof Date ? date : new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Subtract N days from a date and format as "YYYY-MM-DD".
 */
export function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return formatDateISO(d);
}

/**
 * Subtract N months from a date and format as "YYYY-MM-DD".
 * Uses calendar-month arithmetic (e.g., May 5 - 1 month = April 5).
 * For day/month boundary edge cases where the target day doesn't exist
 * (e.g., March 31 - 1 month), falls back to the last day of the target month.
 */
export function monthsAgoISO(n) {
  const d = new Date();
  const targetMonth = d.getMonth() - n;
  d.setMonth(targetMonth);
  // If setMonth overflows (e.g. Mar 31 -> Feb 31 -> Mar 3), clamp to end of prev month
  if (d.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    d.setDate(0); // last day of previous month
  }
  return formatDateISO(d);
}

/**
 * Return today's date formatted as "YYYY-MM-DD".
 */
export function todayISO() {
  return formatDateISO(new Date());
}

/**
 * Add N days to a date and format as "YYYY-MM-DD".
 */
export function daysFromNowISO(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return formatDateISO(d);
}

/**
 * Format a date relative to now (e.g. "3 days ago", "2 months ago").
 */
export function fromNow(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now - d;
  const absDiffMs = Math.abs(diffMs);
  const seconds = Math.floor(absDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (seconds < 60) return 'a few seconds ago';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

/**
 * Locale-aware short date+time format (replaces moment's 'L LT').
 */
export function formatLocaleShort(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format as "MMM YYYY" (e.g. "Jan 2023").
 */
export function formatMonthYear(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
